import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';

import { types as agencyLocationsTypes, actions as agencyLocationsActions } from '@src/ducks/agencyLocations';
import {
    fetchAgencyLocations,
    createAgencyLocation,
    updateAgencyLocation,
    deleteAgencyLocation,
} from '@src/services/agencyLocationsService';
import { handleSagaFailure } from '@src/utils/sagaHelpers';

/**
 * Watch for agency locations related actions
 */
export function * watchAgencyLocations() {
    yield takeLatest(agencyLocationsTypes.AGENCY_LOCATIONS_GET, onGetAgencyLocations);
    yield takeEvery(agencyLocationsTypes.AGENCY_LOCATION_CREATE, onCreateAgencyLocation);
    yield takeEvery(agencyLocationsTypes.AGENCY_LOCATION_UPDATE, onUpdateAgencyLocation);
    yield takeEvery(agencyLocationsTypes.AGENCY_LOCATION_DELETE, onDeleteAgencyLocation);
}

/**
 * Saga to handle fetching agency locations
 */
function * onGetAgencyLocations({ payload }) {
    try {
        const { premierAccessGroupId, params } = payload;

        if (!premierAccessGroupId) {
            throw new Error('Premier Access Group ID is required');
        }

        const response = yield call(fetchAgencyLocations, premierAccessGroupId, params);

        yield put(agencyLocationsActions.getAgencyLocationsSuccess(response));
    } catch (error) {
        yield put(agencyLocationsActions.getAgencyLocationsFailure(error.message || 'Failed to fetch agency locations'));
        yield call(handleSagaFailure, error);
    }
}

/**
 * Saga to handle creating a new agency location
 */
function * onCreateAgencyLocation({ payload }) {
    try {
        const response = yield call(createAgencyLocation, payload);
        
        let location = response;
        if (location && location.data) location = location.data;
        if (location && location.agency_location) location = location.agency_location;

        yield put(agencyLocationsActions.createAgencyLocationSuccess(location));

        // Optionally show success notification
        // yield call(showNotification, 'Agency location created successfully', 'success');
    } catch (error) {
        yield put(agencyLocationsActions.createAgencyLocationFailure(error.message || 'Failed to create agency location'));
        yield call(handleSagaFailure, error);
    }
}

/**
 * Saga to handle updating an agency location
 */
function * onUpdateAgencyLocation({ payload }) {
    try {
        const { locationId, locationData } = payload;

        const response = yield call(updateAgencyLocation, locationId, locationData);

        let location = response;
        if (location && location.data) location = location.data;
        if (location && location.agency_location) location = location.agency_location;

        yield put(agencyLocationsActions.updateAgencyLocationSuccess(location));

        // Optionally show success notification
        // yield call(showNotification, 'Agency location updated successfully', 'success');
    } catch (error) {
        yield put(agencyLocationsActions.updateAgencyLocationFailure(error.message || 'Failed to update agency location'));
        yield call(handleSagaFailure, error);
    }
}

/**
 * Saga to handle deleting an agency location
 */
function * onDeleteAgencyLocation({ payload }) {
    try {
        const { locationId } = payload;

        yield call(deleteAgencyLocation, locationId);

        yield put(agencyLocationsActions.deleteAgencyLocationSuccess(locationId));

        // Optionally show success notification
        // yield call(showNotification, 'Agency location deleted successfully', 'success');
    } catch (error) {
        yield put(agencyLocationsActions.deleteAgencyLocationFailure(error.message || 'Failed to delete agency location'));
        yield call(handleSagaFailure, error);
    }
}
